import axios from 'axios'
import { round, includes, inRange, isFinite } from 'lodash'
import tryCatch from '~/tools/tryCatch'
import { trackEvent } from '~/tools/analytics/googleAnalytics'

// how often the timer functions will fire (milliseconds)
const TIMER_FREQ = 100
// how much to increment timer based on elapsed time of timer (seconds)
const TIMER_INCREMENTER = TIMER_FREQ / 1000
// how often to send the 'buffer' event (seconds)
const BUFFER_INTERVAL = 5

function generateId() {
  const s4 = _ => Math.floor((1 + Math.random()) * 0x10000).toString(16).substring(1)
  return s4() + s4() + '-' + s4() + '-' + s4() + '-' + s4() + '-' + s4() + s4() + s4()
}

function getAnonId() {
  var uid  = window.localStorage.getItem('uid')
  if (uid) return uid

  uid = generateId()

  window.localStorage.setItem('uid', uid)
  return uid
}

function stringify(value) {
  return value ? String(value) : ''
}

class VideoJSBeacon {
  /**
    * @params {Object} args - values necessary for beacon functionality
    * @params {Object} args.player - videojs player instance
    * @params {String} args.videoId -  video id
    * @params {String} args.fileType - 'hls' vs. 'mp4'
    * @params {Object} args.assets - object with video assets, like mpeg files
    * @params {String} args.siteName - site name
    * @params {String} args.clientId - site id
    * @params {String} args.platform - 'website'
    * @params {String} args.userId - user id, or anonymous generated id
    * @params {String} args.apiUrl - beacon api url
    * @params {String} args.environment: develop environment (dev, release, prod)
  */
  constructor(args = {}) {
    this.player = args.player
    this.videoId = args.videoId
    this.videoTitle = args.videoTitle
    this.fileType = args.fileType
    this.assets = args.assets
    this.userId = args.userId || getAnonId()
    this.siteName = args.siteName
    this.clientId = args.clientId
    this.platform = args.platform
    this.apiUrl = args.apiUrl
    this.streamId = generateId()
    this.environment = args.environment
    this.bufferTimer = null
    this.isLiveStream = args.isLiveStream
    this.contentCategory = args.contentCategory
    this.seriesId = args.seriesId
    this.seriesTitle = args.seriesTitle

    this.setVideoListeners()
  }

  setVideoListeners() {
    this.streamFailures()
    this.firstFrame()
    this.buffering()
    this.ads()
    this.ping()
  }

  streamFailures() {
    const initVidTime = Math.floor(this.player.currentTime())
    this.player.on('error', () => {
      let currentTime = Math.floor(this.player.currentTime())
      this.event({
        eventName: initVidTime === currentTime ? 'FAILED_TO_START' : 'DROPPED_STREAM',
        videoPosition: Math.floor(this.player.currentTime()),
        isAdEvent: false
      })
    })
  }

  firstFrame() {
    const dataLayer = window.dataLayer || { push: _ => {} }
    this.player.one('play', tryCatch( () => {
      this.ttFirstFrame = 0
      this.ttFirstFrameTimer = setInterval( () => {
        this.ttFirstFrame = this.handleBufferTimer(this.ttFirstFrame)
      }, TIMER_FREQ)
      
      trackEvent('player-video', 'playVideo', this.videoTitle || this.videoId)
      dataLayer.push({
        'event': 'stream_start',
        'content_uuid': this.videoId,
        'content_name': this.videoTitle,
        ...(this.seriesId && { 'series_uuid': this.seriesId }),
        ...(this.seriesTitle && { 'series_name': this.seriesTitle }),
        'content_category': this.contentCategory,
        'content_is_live': this.isLiveStream
      })
      this.event({
        eventName: 'PLAY',
        videoPosition: Math.floor(this.player.currentTime()),
        isAdEvent: false
      })
      this.endFirstFrame()
    }))
  }

  buffering() {
    this.player.on('waiting', tryCatch( () => {
      if (this.ttFirstFrameTimer) return

      const offset = 0.1
      let lastVideoPosition = this.player.currentTime()

      clearInterval(this.bufferTimer)

      this.bufferTimer = setInterval( () => {
        if (
          (this.player.currentTime() <= (lastVideoPosition + offset)) &&
          !this.player.paused())
        {
          this.totalBufferTime = this.handleBufferTimer(this.totalBufferTime)
        } else {
          clearInterval(this.bufferTimer)
        }
      }, TIMER_FREQ)
    }))
  }

  handleBufferTimer(timer)  {
    let incrementedTimer = timer + TIMER_INCREMENTER
    let roundedTime = round(incrementedTimer, 2)
    if (roundedTime % BUFFER_INTERVAL === 0 && this.player && this.player.currentTime) {
      try{
        this.event({
          eventName: 'BUFFERING',
          videoPosition: Math.floor(this.player.currentTime()),
          isAdEvent: false
        })
      }catch(error){
        clearInterval(this.bufferTimer)
      }
    }
    return incrementedTimer
  }

  endFirstFrame() {
    this.player.one('timeupdate', () => {
      clearInterval(this.ttFirstFrameTimer)
      this.ttFirstFrameTimer = null
      // buffer timer picks up where the first frame timer left off
      this.totalBufferTime = this.ttFirstFrame || 0

      this.event({
        eventName: 'FIRST_FRAME',
        videoPosition: Math.floor(this.player.currentTime()),
        isAdEvent: false,
        ttFirstFrame: this.ttFirstFrame,
        bufferHealth: this.getRemainingBufferTime()
      })
    })
  }

  ads() {
    this.player.on('adsready', () => {
      this.player.ima.addEventListener(window.google.ima.AdEvent.Type.STARTED, () => {
        this.event({
          eventName: 'AD_REQUEST',
          videoPosition: Math.ceil(this.player.currentTime()),
          isAdEvent: true,
          adPosition: 1
        })

        this.event({
          eventName: 'AD_IMPRESSION',
          videoPosition: Math.ceil(this.player.currentTime()),
          isAdEvent: true,
          adPosition: 1
        })
      })
    })
  }

  ping() {
    var pingTimes = []
    this.player.on('timeupdate', () => {
      var time = Math.floor(this.player.currentTime())
      if (time >= 30 && time % 30 === 0 && !includes(pingTimes, time)) {
        pingTimes.push(time)
        this.event({
          eventName: 'PING',
          videoPosition: time,
          isAdEvent: false,
          resHeight: this.player.videoHeight(),
          resWidth: this.player.videoWidth(),
          bufferHealth: this.getRemainingBufferTime(),
          bitrate: this.getBitrate()
        })
      }
    })
  }

  getBitrate() {
    return tryCatch( () => {
      var bandwidth
      if (this.fileType === 'hls') {
        bandwidth = round(this.player.tech_.hls.selectPlaylist().attributes.BANDWIDTH / 1000)
      } else {
        let currentMpegUrl = this.player.currentSrc().split('?')[0]
        this.assets.mpeg.forEach( mpeg => {
          let mpegUrl = mpeg.url.split('?')[0]
          if (currentMpegUrl === mpegUrl) {
            bandwidth = mpeg.bitrate
          }
        })
      }
      return bandwidth
    })()
  }

  getRemainingBufferTime() {
    return tryCatch( () => {
      let currentTime = this.player.currentTime()
      let runtime = this.player.duration()
      let bufferedRanges = this.player.buffered()

      if(!bufferedRanges.start || !bufferedRanges.end) return;
      
      for (let i = 0; i < bufferedRanges.length; i++) {
        let bufferedRangeStart = bufferedRanges.start(i)
        let bufferedRangeEnd = bufferedRanges.end(i)
        if (inRange(currentTime, bufferedRangeStart, bufferedRangeEnd)) {
          var ret = round(runtime - bufferedRangeEnd)
          if (isFinite(ret)) {
            return ret
          }
        }
      }
    })()
  }

  event(eventObject) {
    var postObject = [{
      aid: this.siteName,
      cid: this.clientId,
      pfm: this.platform,
      uid: this.userId,
      vid: this.videoId,
      url: eventObject.url || document.location.href,
      ref: eventObject.ref || document.referrer,
      pa: eventObject.eventName,
      vpos: stringify(eventObject.videoPosition),
      environment: this.environment,
      dp1: eventObject.dp1 || '',
      dp2: eventObject.dp2 || '',
      dp3: eventObject.dp3 || '',
      dp4: eventObject.dp4 || '',
      dp5: eventObject.dp5 || '',
      apos: eventObject.isAdEvent === true ? stringify(eventObject.videoPosition) : '',
      apod: eventObject.isAdEvent === true ? stringify(eventObject.adPosition) : '',
      profid: '',
      player: eventObject.playerType || 'native',
      media_type: eventObject.mediaType || 'VIDEO',
      stream_id: this.streamId,
      embedurl: '',
      ttfirstframe: stringify(eventObject.ttFirstFrame),
      bitrate: stringify(eventObject.bitrate),
      connectionspeed: '',
      resolutionheight: stringify(eventObject.resHeight),
      resolutionwidth: stringify(eventObject.resWidth),
      bufferhealth: stringify(eventObject.bufferHealth)
    }]

    axios({
      url: this.apiUrl,
      method: 'post',
      contentType: 'application/x-www-form-urlencoded',
      dataType: 'text',
      data: postObject
    }).catch( e => console.error(e) )
  }
}

export default VideoJSBeacon
